import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import DashboardPanel from './DashboardPanel';
import { projectName } from './qualityOverviewCopy';

export const formatDate = (value, language) =>
  value
    ? new Date(value).toLocaleString(language === 'es' ? 'es' : 'en', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '—';

export function FailureBreakdown({ data, copy, onProject }) {
  return (
    <DashboardPanel className="p-6">
      <h3 className="text-lg font-semibold text-slate-900">{copy.breakdown}</h3>
      <p className="text-sm text-slate-500 mt-1 mb-5">{copy.breakdownHint}</p>
      <div className="flex gap-5 text-xs text-slate-500 mb-4">
        <span>
          <span className="inline-block w-2 h-2 rounded-full bg-violet-600 mr-2" />
          {copy.api}
        </span>
        <span>
          <span className="inline-block w-2 h-2 rounded-full bg-rose-500 mr-2" />
          {copy.flow}
        </span>
      </div>
      {!data.failure_breakdown.length && (
        <p className="text-sm text-slate-500 py-8">{copy.noFailures}</p>
      )}
      <div className="space-y-5">
        {data.failure_breakdown.map((row) => (
          <button
            key={row.id}
            type="button"
            onClick={() => onProject(row.id)}
            className="w-full text-left group"
            aria-label={`${copy.project}: ${projectName(row, copy)}`}
          >
            <div className="flex justify-between text-sm mb-2 gap-3">
              <span className="font-medium text-slate-700 group-hover:text-violet-700 truncate">
                {projectName(row, copy)}
              </span>
              <span className="font-semibold tabular-nums">{row.failed}</span>
            </div>
            <div
              className="flex h-2.5 rounded-full bg-slate-100 overflow-hidden"
              aria-hidden="true"
            >
              <span
                className="bg-violet-600"
                style={{ width: `${(100 * row.api_failures) / data.summary.failed}%` }}
              />
              <span
                className="bg-rose-500"
                style={{ width: `${(100 * row.flow_failures) / data.summary.failed}%` }}
              />
            </div>
            <span className="sr-only">
              {row.api_failures} {copy.api}, {row.flow_failures} {copy.flow}
            </span>
          </button>
        ))}
      </div>
    </DashboardPanel>
  );
}

export function ProjectHealth({ data, copy, language, onProject }) {
  return (
    <DashboardPanel className="overflow-hidden">
      <div className="px-6 pt-6 pb-4">
        <h3 className="text-lg font-semibold text-slate-900">{copy.health}</h3>
        <p className="text-sm text-slate-500 mt-1">{copy.healthHint}</p>
      </div>
      <div className="overflow-auto max-h-[440px]">
        <table className="w-full text-sm text-left" aria-label={copy.health}>
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>
              {[
                copy.project,
                copy.tests,
                copy.completedShort,
                copy.successRate,
                copy.failingShort,
                copy.neverShort,
                copy.lastRun,
              ].map((title) => (
                <th key={title} className="px-5 py-3 font-medium whitespace-nowrap">
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.project_health.map((row) => (
              <tr key={row.id} className="border-t border-slate-100">
                <td className="px-5 py-4">
                  <button
                    type="button"
                    onClick={() => onProject(row.id)}
                    className="text-violet-700 font-medium text-left hover:underline"
                  >
                    {projectName(row, copy)}
                  </button>
                </td>
                <td className="px-5 py-4 tabular-nums">{row.total_tests}</td>
                <td className="px-5 py-4 tabular-nums">{row.passed + row.failed}</td>
                <td className="px-5 py-4 tabular-nums">
                  {row.success_rate === null ? '—' : `${row.success_rate}%`}
                </td>
                <td
                  className={`px-5 py-4 tabular-nums ${row.currently_failing ? 'text-rose-700 font-semibold' : 'text-slate-500'}`}
                >
                  {row.currently_failing}
                </td>
                <td className="px-5 py-4 tabular-nums text-slate-500">{row.never_run}</td>
                <td className="px-5 py-4 whitespace-nowrap text-slate-500">
                  {formatDate(row.last_run_at, language)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data.project_health.length && (
          <p className="px-6 py-8 text-sm text-slate-500">{copy.noProjects}</p>
        )}
      </div>
    </DashboardPanel>
  );
}

export function RecentActivity({ data, copy, language, onOpen, opening }) {
  return (
    <DashboardPanel className="p-6">
      <h3 className="text-lg font-semibold text-slate-900">{copy.activity}</h3>
      <p className="text-sm text-slate-500 mt-1 mb-4">{copy.activityHint}</p>
      {!data.recent.length && <p className="text-sm text-slate-500 py-6">{copy.noActivity}</p>}
      <div className="divide-y divide-slate-100">
        {data.recent.map((row, index) => {
          const passed = ['pass', 'passed'].includes(row.status);
          const failed = ['fail', 'failed', 'error'].includes(row.status);
          return (
            <button
              key={`${row.test.type}-${row.id || index}`}
              type="button"
              disabled={Boolean(opening)}
              onClick={() => onOpen(row.test)}
              aria-label={`${copy.history}: ${row.test.name}`}
              className="w-full py-4 flex items-center justify-between gap-4 text-left group disabled:opacity-50"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate group-hover:text-violet-700">
                  {row.test.name}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {row.test.type === 'api_test' ? copy.api : copy.flow} ·{' '}
                  {formatDate(row.at, language)}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span
                  className={`text-xs rounded-full px-2.5 py-1 ${passed ? 'bg-emerald-50 text-emerald-700' : failed ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-600'}`}
                >
                  {copy.status[row.status] || row.status.replace(/_/g, ' ')}
                </span>
                <ArrowUpRight className="w-4 h-4 text-slate-400" />
              </div>
            </button>
          );
        })}
      </div>
    </DashboardPanel>
  );
}
